import { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from 'maplibre-gl'
import type { Journey } from '@/types/journey'
import type { LiveUser } from '@/types/liveview'
import type { ProximiioFeatureCollection, ProximiioStyleResponse } from '@/types/proximi'
import { levelAwareLayers, MAIN_SOURCE_ID, retargetFilter, toMapLibreStyle } from '@/lib/style'
import {
  addJourneyLayers,
  buildJourneyLine,
  buildJourneyPoints,
  journeyBounds,
  JOURNEY_LAYERS,
  JOURNEY_LINE_SOURCE,
  JOURNEY_POINT_SOURCE,
} from '@/components/map/journeyLayers'
import {
  ACCURACY_SOURCE,
  addLiveLayers,
  CAPTURE_CELLS_SOURCE,
  CAPTURE_LAYERS,
  CAPTURE_POINTS_SOURCE,
  CAPTURE_ROUTE_SOURCE,
  visibleUsers,
  buildAccuracyFeatures,
  buildJourneyRingFeatures,
  buildTrailFeatures,
  buildUserFeatures,
  INTERACTIVE_LAYERS,
  JOURNEY_RINGS_SOURCE,
  TRAILS_SOURCE,
  USERS_SOURCE,
  USER_PULSE_LAYER,
  type UserRenderOptions,
} from '@/components/map/liveLayers'

export interface CameraRequest {
  lng: number
  lat: number
  /** Bumped by the parent so a repeat of the same coordinates still moves. */
  nonce: number
  /** Ease duration in ms. Short while following, longer for a deliberate jump. */
  duration: number
  /** Zoom in to at least this level; omitted keeps the current zoom. */
  minZoom?: number
}

interface LiveMapProps {
  style: ProximiioStyleResponse
  features: ProximiioFeatureCollection
  users: LiveUser[]
  options: UserRenderOptions
  camera: CameraRequest | null
  /** Bumped by the parent to frame every currently visible device. */
  fitNonce: number
  /** Capture plan overlay; `null` hides every capture layer. */
  capture: CaptureOverlay | null
  /** The journey being looked at, drawn read-only; `null` hides it. */
  journey: Journey | null
  /** Bumped by the parent to frame the selected journey. */
  journeyFitNonce: number
  onSelectUser: (userId: string | null) => void
}

export interface CaptureOverlay {
  points: GeoJSON.FeatureCollection
  cells: GeoJSON.FeatureCollection
  /** Dashed line from the surveyor to the next target, when both are known. */
  route: GeoJSON.FeatureCollection
}

/** Bounds covering every coordinate in the collection. */
function boundsOfFeatures(
  collection: GeoJSON.FeatureCollection,
): maplibregl.LngLatBounds | null {
  const bounds = new maplibregl.LngLatBounds()
  let seen = false
  const visit = (coords: unknown): void => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      bounds.extend(coords as [number, number])
      seen = true
      return
    }
    for (const child of coords) visit(child)
  }
  for (const feature of collection.features) {
    if (feature.geometry && 'coordinates' in feature.geometry) {
      visit(feature.geometry.coordinates)
    }
  }
  return seen ? bounds : null
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function LiveMap({
  style,
  features,
  users,
  options,
  camera,
  fitNonce,
  capture,
  journey,
  journeyFitNonce,
  onSelectUser,
}: LiveMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  // State, not a ref: the data effects below must re-run once the style has
  // loaded, otherwise the first level and the first batch of users are dropped
  // on the floor and only appear on the next change.
  const [ready, setReady] = useState(false)
  const framedVenueRef = useRef(false)

  // Original filters, captured once: every level change is computed from these
  // rather than from the previous rewrite, so levels never compound.
  const baseFilters = useMemo(() => levelAwareLayers(style), [style])

  // --- map lifecycle -------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const mapStyle = toMapLibreStyle(style, features)
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: mapStyle,
      center: style.center ?? [18.555, 48.4437],
      zoom: style.zoom ?? 17,
      bearing: style.bearing ?? 0,
      pitch: style.pitch ?? 0,
      attributionControl: { compact: true },
      hash: false,
    })

    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right')
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left')

    // The breathing halo under each online dot. Driven here rather than from its
    // own effect so the frame is guaranteed to be cancelled before `map.remove()`
    // — touching a removed map throws inside MapLibre.
    let pulseFrame = 0
    const animatePulse = () => {
      let last = 0
      const tick = (time: number) => {
        pulseFrame = requestAnimationFrame(tick)
        // ~20fps is plenty for a halo and leaves the render budget alone.
        if (time - last < 50) return
        last = time
        if (!map.getLayer(USER_PULSE_LAYER)) return
        const phase = (time % 2400) / 2400
        map.setPaintProperty(USER_PULSE_LAYER, 'circle-radius', 10 + phase * 22)
        map.setPaintProperty(USER_PULSE_LAYER, 'circle-opacity', 0.22 * (1 - phase))
      }
      pulseFrame = requestAnimationFrame(tick)
    }

    map.on('load', () => {
      // Journeys first, so the live dots and their trails draw on top of the
      // route they are walking rather than underneath it.
      addJourneyLayers(map)
      addLiveLayers(map)
      setReady(true)
      if (!prefersReducedMotion()) animatePulse()
    })

    // Missing sprite entries are routine on customer styles; a transparent
    // 1×1 keeps MapLibre from logging one warning per symbol per frame.
    map.on('styleimagemissing', (event) => {
      if (map.hasImage(event.id)) return
      map.addImage(event.id, { width: 1, height: 1, data: new Uint8Array(4) })
    })

    for (const layerId of INTERACTIVE_LAYERS) {
      map.on('click', layerId, (event) => {
        const userId = event.features?.[0]?.properties?.user_id
        if (typeof userId === 'string') {
          onSelectUser(userId)
          event.originalEvent.stopPropagation()
        }
      })
      map.on('mouseenter', layerId, () => {
        map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = ''
      })
    }

    map.on('click', (event) => {
      const hits = map.queryRenderedFeatures(event.point, { layers: INTERACTIVE_LAYERS })
      if (hits.length === 0) onSelectUser(null)
    })

    mapRef.current = map
    return () => {
      cancelAnimationFrame(pulseFrame)
      setReady(false)
      map.remove()
      mapRef.current = null
    }
    // Deliberately mount-only: the style is swapped through `setStyle` below
    // rather than by tearing the map down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- base features (the venue itself) ------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    const source = map.getSource(MAIN_SOURCE_ID) as GeoJSONSource | undefined
    source?.setData(features)

    // Open on the venue, not on the style's `center`.
    //
    // Proximi.io styles are commonly derived from the stock default, whose
    // centre is wherever the template was authored — the org tested here has a
    // style centred on Helsinki and a venue in Slovakia, 1900 km away, so
    // trusting `style.center` opens the map on empty basemap. The geometry knows
    // where the venue is; use it, once, and never fight the viewer afterwards.
    if (framedVenueRef.current || features.features.length === 0) return
    const bounds = boundsOfFeatures(features)
    if (!bounds) return
    framedVenueRef.current = true
    map.fitBounds(bounds, {
      // A small venue has near-zero-area bounds, which `fitBounds` would answer
      // with maximum zoom.
      maxZoom: 20,
      padding: 64,
      duration: 0,
    })
  }, [features, ready])

  // --- floor level ---------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready || options.level === null) return
    for (const { id, filter } of baseFilters) {
      if (!map.getLayer(id)) continue
      map.setFilter(id, retargetFilter(filter, options.level, id) ?? null)
    }
  }, [baseFilters, options.level, ready])

  // --- live users ----------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    ;(map.getSource(USERS_SOURCE) as GeoJSONSource | undefined)?.setData(
      buildUserFeatures(users, options),
    )
    ;(map.getSource(ACCURACY_SOURCE) as GeoJSONSource | undefined)?.setData(
      buildAccuracyFeatures(users, options),
    )
    ;(map.getSource(TRAILS_SOURCE) as GeoJSONSource | undefined)?.setData(
      buildTrailFeatures(users, options),
    )
    ;(map.getSource(JOURNEY_RINGS_SOURCE) as GeoJSONSource | undefined)?.setData(
      buildJourneyRingFeatures(users, options),
    )
  }, [users, options, ready])

  // --- capture plan ---------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return

    const visibility = capture ? 'visible' : 'none'
    for (const id of CAPTURE_LAYERS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility)
    }
    if (!capture) return
    ;(map.getSource(CAPTURE_POINTS_SOURCE) as GeoJSONSource | undefined)?.setData(capture.points)
    ;(map.getSource(CAPTURE_CELLS_SOURCE) as GeoJSONSource | undefined)?.setData(capture.cells)
    ;(map.getSource(CAPTURE_ROUTE_SOURCE) as GeoJSONSource | undefined)?.setData(capture.route)
  }, [capture, ready])

  // --- the selected journey, read-only -------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return

    const visibility = journey ? 'visible' : 'none'
    for (const id of JOURNEY_LAYERS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility)
    }
    if (!journey) return
    ;(map.getSource(JOURNEY_LINE_SOURCE) as GeoJSONSource | undefined)?.setData(
      buildJourneyLine(journey, options.level),
    )
    ;(map.getSource(JOURNEY_POINT_SOURCE) as GeoJSONSource | undefined)?.setData(
      buildJourneyPoints(journey, options.level),
    )
  }, [journey, options.level, ready])

  // --- frame the selected journey -------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready || journeyFitNonce === 0) return
    const bounds = journeyBounds(journey)
    if (!bounds) return
    map.fitBounds(bounds, {
      // A journey that never leaves one room has near-zero-area bounds, which
      // `fitBounds` would answer with maximum zoom.
      maxZoom: 20,
      padding: { top: 80, right: 80, bottom: 80, left: 80 },
      duration: 700,
    })
    // Only when the viewer picks one, not every time the journey object is
    // rebuilt by a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyFitNonce, ready])

  // --- camera ---------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !camera) return
    map.easeTo({
      center: [camera.lng, camera.lat],
      zoom: camera.minZoom ? Math.max(map.getZoom(), camera.minZoom) : map.getZoom(),
      duration: camera.duration,
      // Following is a background motion, not something the viewer asked for
      // frame by frame; it must never fight a hand on the map.
      essential: false,
    })
  }, [camera])

  // --- fit every visible device --------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready || fitNonce === 0) return

    const shown = visibleUsers(users, options)
    if (shown.length === 0) return

    const bounds = new maplibregl.LngLatBounds()
    for (const user of shown) bounds.extend([user.lng, user.lat])

    map.fitBounds(bounds, {
      // A single device has zero-area bounds, which `fitBounds` would answer
      // with maximum zoom; cap it at something a person can read.
      maxZoom: 20,
      padding: { top: 80, right: 80, bottom: 80, left: 80 },
      duration: 700,
    })
    // `users`/`options` are read but deliberately not dependencies: this fires
    // only when the viewer asks for it, not every time somebody moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitNonce, ready])

  return <div ref={containerRef} className="absolute inset-0" />
}
